
import React, { useState } from 'react';
import './Experience.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faCalendarAlt, faChevronRight, faDatabase, faTerminal, faCode, faMicrochip, faServer, faEye, faDollarSign } from '@fortawesome/free-solid-svg-icons';
import {
  faNodeJs,
  faReact,
  faJava,
  faAws,
  faPython,
  faDocker,
  faGitAlt,
} from '@fortawesome/free-brands-svg-icons';

const tagsWithIcons = {
  Node: faNodeJs,
  'React.JS': faReact,
  Java: faJava,
  AWS: faAws,
  Python: faPython,
  Docker: faDocker,
  SQL: faDatabase,
  PowerShell: faTerminal,
  'REST APIs': faCode,
  'Raspberry Pi': faMicrochip,
  'Computer Vision': faEye,
  Operations: faServer,
  Fundraising: faDollarSign,
  Git: faGitAlt,
  Kubernetes: faServer,
  'AWS Textract': faAws,
  'Spring Boot': faJava,
  Neo4J: faDatabase,
  PostgreSQL: faDatabase,
};

const experienceData = [
  {
    id: 'aws',
    role: "Software Engineer Intern",
    company: "Amazon Web Services",
    companyUrl: "https://aws.amazon.com/",
    location: "Seattle, Washington",
    date: "May 2025 - August 2025",
    bullets: [
      "Architected modular Infrastructure-as-Code (IaC) frameworks for EC2 UltraServers using AWS CDK and TypeScript, enabling multi-regional deployments, future GPU scalability, and 80% reduction in deployment latency",
      "Designed a high-throughput, low-latency telemetry pipeline processing 200K logs/hour per GPU across 8 AWS regions, integrating NVIDIA GB200 UltraServer metrics into dashboards to reduce MTTR by 4 hours weekly",
      "Built a reusable Python CLI tool converting QuickSight JSON visuals into TypeScript CDK stacks, automating dashboard generation and enabling 98% faster IaC deployments, resulting in org-wide tool adoption",
      "Collaborated with engineering and operational teams on design reviews, integration and deployment validation, and cross-region rollout testing, ensuring reliability and scalability of AWS infrastructure deployments"
    ],
    tags: ["AWS CDK", "TypeScript", "Python", "EC2"]
  },
  {
    id: 'comtech',
    role: "Software Engineer Intern",
    company: "Comtech",
    companyUrl: "https://comtech.com/capability/voip-e9-1-1/",
    location: "Seattle, Washington",
    date: "June 2024 - August 2024",
    bullets: [
      "Enhanced a fault-tolerant provisioning portal in React.js, integrating Okta SSO through test-driven development (TDD) to harden security and support higher concurrency, achieving 99% adoption and 50% faster logins",
      "Executed zero-downtime database migration from Oracle and MySQL to PostgreSQL with Spring Boot and feature flag deployment, improving runtime performance by 80% and reducing operational costs by 60%",
      "Drove full system design including unit and integration test suite for the portal, collaborated with a cross-functional team of 7 developers on architecture planning and code reviews across various projects, focusing on CI/CD"
    ],
    tags: ["React.js", "Spring Boot", "PostgreSQL", "Okta", "TDD"]
  },
  {
    id: 'stealth',
    role: "Software Engineer Intern",
    company: "Stealth",
    companyUrl: null,
    location: "Remote",
    date: "April 2024 - June 2024",
    bullets: [
      "Architected a serverless intelligent document processing (IDP) pipeline on AWS using Lambda (Python), Textract, and S3, automating ingestion and validation of monthly compliance audits from 12 international sources",
      "Engineered a recommendation engine on a Neo4j knowledge graph to flag latent risks within compliance workflows spanning 28 countries, leveraging A/B testing to validate optimized Cypher queries and boosting validation efficiency by 35%",
      "Automated deployment for containerized IDP and graph model components by orchestrating CI/CD pipeline with Jenkins, working directly with regional managers to standardize global workflows"
    ],
    tags: ["AWS Lambda", "Python", "Neo4j", "Textract", "Jenkins"]
  },
  {
    id: 'automotive',
    role: "Undergraduate Researcher",
    company: "Automotive Ethics",
    companyUrl: "https://www.stonybrook.edu/commcms/vertically-integrated-projects/teams/_team_page/team_page.php?team=Automotive%20Ethics",
    location: "Stony Brook, New York",
    date: "May 2023 - May 2025",
    bullets: [
      "Co-led a 9-member research team in partnership with Daimler Mercedes to develop a simulation platform for modeling and validating 50+ ethical dilemmas for embedded systems in autonomous vehicles",
      "Built core simulation infrastructure on ROS, integrating OpenStreetMap data to procedurally generate test scenarios and a visualization portal in React to analyze results, accelerating testing cycles by 3x"
    ],
    tags: ["React.js", "Python"]
  },
  {
    id: 'sbm',
    role: "Systems Engineer Intern",
    company: "Stony Brook Medicine",
    companyUrl: "https://extranet.stonybrookmedicine.edu/IT-infrastructure/services/system-admin",
    location: "Stony Brook, New York",
    date: "September 2022 - May 2025",
    bullets: [
      "Automated data retrieval from 2,500 VMs using multithreading and APIs, saving 20 hours of manual work per week",
      "Developed an event-driven microservice agent to monitor and notify updates for 3 external dependent services, reducing system downtime by 40%, and enhancing maintenance and reliability"
    ],
    tags: ["Python", "SQL", "PowerShell", "Docker", "REST APIs"]
  },

];

const Experience = () => {
  const [selectedId, setSelectedId] = useState(experienceData[0].id);

  const selectedExperience = experienceData.find(exp => exp.id === selectedId);

  return (
    <section className="experience" id="experience">
      <div className="experience-container">
        {/* Left Pane: Filter/List List */}
        <div className="experience-list">
          {experienceData.map((exp) => (
            <div
              key={exp.id}
              className={`experience-list-item ${selectedId === exp.id ? 'active' : ''}`}
              onClick={() => setSelectedId(exp.id)}
            >
              <h4 className="list-role">{exp.role}</h4>
              <span className="list-company">{exp.company}</span>
              <span className="list-date">{exp.date}</span>
            </div>
          ))}
        </div>

        {/* Right Pane: Detailed View */}
        <div className="experience-detail">
          <div className="detail-header">
            <h3>
              {selectedExperience.role} <span className="at-symbol">@</span>
              {selectedExperience.companyUrl ? (
                <a href={selectedExperience.companyUrl} target="_blank" rel="noopener noreferrer" className="company-link">
                  {selectedExperience.company}
                </a>
              ) : (
                <span className="company-link"> {selectedExperience.company}</span>
              )}
            </h3>

            <div className="detail-meta">
              <span className="meta-widget">
                <FontAwesomeIcon icon={faMapMarkerAlt} /> {selectedExperience.location}
              </span>
              <span className="job-date">
                {selectedExperience.date}
              </span>
            </div>
          </div>

          <div className="detail-content">
            <ul>
              {selectedExperience.bullets.map((bullet, idx) => (
                <li key={idx}>
                  <FontAwesomeIcon icon={faChevronRight} className="bullet-icon" /> {bullet}
                </li>
              ))}
            </ul>
            <div className="tags">
              {selectedExperience.tags.map((tag, idx) => (
                <span key={idx} className="tag">
                  <FontAwesomeIcon icon={tagsWithIcons[tag] || faCode} /> {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
